import { AlertTriangleIcon, CheckCircleBrokenIcon, InfoCircleIcon, XCloseIcon } from '@prophecy/icons';
import { BaseColor, theme } from '@prophecy/theme';
import { preventAutoCloseTriggerFocus } from '@prophecy/utils/dom';
import { useFocusTrap } from '@prophecy/utils/react/focus';
import utilify from '@prophecy/utils/react/utilify';
import { Close as RadixClose, DialogContentProps, Overlay, Portal, Root, Trigger } from '@radix-ui/react-dialog';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import { Stack } from '../Layout';
import { Text } from '../Typography/Text';
import { useBlockingDialogBodyFlag } from './hooks';
import {
  CancelButton,
  OkButton,
  StyledChildrenWrap,
  StyledContent,
  StyledDialog,
  StyledFooter,
  StyledIconContainer,
  StyledLabel,
  StyledOverlay,
  StyledCrossIcon
} from './styled';
import { DialogComponentNames, tokens } from './tokens';
import {
  DialogResponseAction,
  DialogButtonProps,
  DialogProps,
  DialogSize,
  DialogSizeType,
  DialogUtilConfig,
  IconPlacement
} from './types';

const Close = RadixClose;

//Exposing `DialogTitleContainer` for nested style overrides of the title container.
export const DialogTitleContainer = styled(Stack)``;

type ContentProps = DialogContentProps & {
  size: DialogSizeType;
  open: boolean;
  footer?: React.ReactNode;
  overlayClassName?: string;
  overlayStyle?: React.CSSProperties;
  showCloseIcon?: boolean;
  rootClassName?: string;
  container?: React.RefObject<HTMLDivElement>;
  'data-test-id': string;
};

const DialogContent = React.forwardRef<HTMLDivElement, ContentProps>(
  (
    { size, footer, overlayClassName, overlayStyle, children, open, showCloseIcon, rootClassName, container, ...props },
    forwardedRef
  ) => {
    return (
      <Portal>
        <Overlay className={rootClassName}>
          <StyledOverlay
            data-component={DialogComponentNames.overlay}
            className={overlayClassName}
            style={overlayStyle}
            data-state={open ? 'open' : 'closed'}
          />
          <StyledDialog data-component={DialogComponentNames.content} {...props} size={size} ref={forwardedRef}>
            <Stack direction='vertical' height='100%' align='space-between' alignY='stretch'>
              <StyledContent grow='1' ref={container} tabIndex={-1}>
                {children}
              </StyledContent>
              {footer && <StyledFooter>{footer}</StyledFooter>}
            </Stack>

            {showCloseIcon && (
              <Close asChild>
                <StyledCrossIcon
                  aria-label='close-dialog'
                  icon={<XCloseIcon type='default' color={theme.colors.gray400} />}
                  variant='tertiaryGray'
                  shape='circle'
                  size={size}
                  data-test-id='dialog-close-button'
                />
              </Close>
            )}
          </StyledDialog>
        </Overlay>
      </Portal>
    );
  }
);

export const IconMap = {
  Warning: AlertTriangleIcon,
  Alert: AlertTriangleIcon,
  Info: InfoCircleIcon,
  Success: CheckCircleBrokenIcon
};

const AlignMap = {
  center: 'center',
  left: 'start',
  right: 'space-between'
};
export const Dialog = ({
  size = DialogSize.m,
  children,
  trigger,
  closeButton = { children: null },
  okButton = { children: null },
  ButtonsContainer,
  footer,
  closable = true,
  open,
  onClose,
  icon,
  onOk,
  iconPlacement = IconPlacement.left,
  title,
  subTitle,
  overlayClassName,
  overlayStyle,
  showCloseIcon,
  className,
  style,
  rootClassName,
  isBlocking,
  onCloseAutoFocus,
  onOpenAutoFocus,
  ...restProps
}: DialogProps) => {
  useBlockingDialogBodyFlag(isBlocking, open);

  const contentContainer = useFocusTrap(Boolean(open));
  const okButtonRef = useRef<HTMLButtonElement | null>(null);
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const containerElm = contentContainer.current;
    if (!containerElm || !open) return;
    if (!okButtonRef.current || !cancelButtonRef.current) return;
    // focus on primary dialog button if the content doesn't have any input elements
    if (!containerElm.querySelector('input, select, textarea')) {
      const button = okButtonRef.current || cancelButtonRef.current;
      button.focus();
    }
  }, [contentContainer, open]);

  const onOpenChange = (open: boolean) => {
    if (!open && closable) {
      onClose?.(DialogAction.close);
    }
  };
  const isBlock = Boolean(closeButton?.block || okButton?.block);

  //button block property overrieds some of the required styles, override to false in button props.

  const buttons = (
    <>
      {okButton && (
        <OkButton
          variant='primary'
          {...okButton}
          data-test-id={okButton['data-test-id'] ?? 'dialog-ok-button'}
          ref={okButtonRef}
          block={false}
          onClick={onOk}
          isBlock={isBlock}>
          {okButton.children ? okButton.children : 'OK'}
        </OkButton>
      )}
      {closeButton && (
        <Close
          onClick={(e) => {
            e.preventDefault();
            onClose?.(DialogAction.cancelButton);
          }}
          asChild>
          <CancelButton
            variant={closeButton.danger ? 'secondary' : 'secondaryGray'}
            {...(closeButton as Omit<DialogButtonProps, 'variant'>)}
            data-test-id={closeButton['data-test-id'] ?? 'dialog-cancel-button'}
            ref={cancelButtonRef}
            block={false}
            isBlock={isBlock}
            onClick={closeButton.onClick}>
            {closeButton.children ? closeButton.children : 'Cancel'}
          </CancelButton>
        </Close>
      )}
    </>
  );
  const buttonWrapper = ButtonsContainer ? (
    <ButtonsContainer>{buttons}</ButtonsContainer>
  ) : (
    <Stack direction='horizontal' gap={theme.spaces.x12} align='end' alignY='center'>
      {buttons}
    </Stack>
  );
  return (
    <Root open={open} {...restProps} onOpenChange={onOpenChange}>
      {trigger && <Trigger asChild>{trigger}</Trigger>}
      <DialogContent
        className={className}
        style={style}
        rootClassName={rootClassName}
        overlayClassName={overlayClassName}
        overlayStyle={overlayStyle}
        size={size}
        footer={footer}
        open={Boolean(open)}
        container={contentContainer}
        showCloseIcon={showCloseIcon}
        onCloseAutoFocus={onCloseAutoFocus || preventAutoCloseTriggerFocus}
        onOpenAutoFocus={onOpenAutoFocus}
        data-test-id={restProps['data-test-id'] ?? 'dialog-content'}>
        <Stack
          direction={iconPlacement === IconPlacement.center ? 'vertical' : 'horizontal'}
          gap={theme.spaces.x16}
          align={AlignMap[iconPlacement]}
          alignY={subTitle ? 'start' : 'center'}>
          {iconPlacement === 'left' || iconPlacement === 'center' ? icon : null}
          <DialogTitleContainer direction='vertical' gap={theme.spaces.x12} align='start' alignY='start'>
            {title && (
              <Text
                data-component={DialogComponentNames.title}
                weight={tokens.Dialog.Content.headingFontWeight}
                tone={tokens.Dialog.Content.headingColor}
                level='lg'>
                {title}
              </Text>
            )}
            {subTitle && (
              <StyledLabel data-component={DialogComponentNames.subTitle} level='sm'>
                {subTitle}
              </StyledLabel>
            )}
          </DialogTitleContainer>
          {iconPlacement === 'right' ? icon : null}
        </Stack>
        <StyledChildrenWrap>{children}</StyledChildrenWrap>
        {buttonWrapper}
      </DialogContent>
    </Root>
  );
};

const dialog = utilify<DialogUtilConfig & { Component?: typeof Dialog }, Promise<DialogResponseAction>>(
  ({ props, render, unmount }) => {
    return new Promise((resolve) => {
      const renderDialog = ({ children, ..._props }: DialogUtilConfig) => {
        const onOk = () => {
          _props.onOk?.();
          resolve(DialogAction.okButton);
          unmount();
        };
        const onClose = (action?: DialogResponseAction) => {
          _props.onClose?.(action);
          resolve(action ?? DialogAction.close);
          unmount();
        };

        const CustomDialog = props.Component || Dialog;
        render(
          <CustomDialog {..._props} open={true} onOk={onOk} onClose={onClose} isBlocking>
            {typeof children === 'function' ? children({ onOk, onClose }) : children}
          </CustomDialog>,
          { unmountOnNavigation: true }
        );
      };

      renderDialog(props);
    });
  }
);

Dialog.alert = ({ iconPlacement = 'left', ...props }: DialogUtilConfig) =>
  dialog({
    ...props,
    iconPlacement,
    icon: (
      <StyledIconContainer iconPlacement={iconPlacement} tone={BaseColor.error}>
        <IconMap.Alert type='default' />
      </StyledIconContainer>
    )
  });

Dialog.success = ({ iconPlacement = 'left', ...props }: DialogUtilConfig) =>
  dialog({
    iconPlacement,
    icon: (
      <StyledIconContainer iconPlacement={iconPlacement} tone={BaseColor.success}>
        <IconMap.Success type='default' />
      </StyledIconContainer>
    ),
    closeButton: null,
    ...props
  });

Dialog.warning = ({ iconPlacement = 'left', ...props }: DialogUtilConfig) =>
  dialog({
    iconPlacement,
    icon: (
      <StyledIconContainer iconPlacement={iconPlacement} tone={BaseColor.warning}>
        <IconMap.Warning type='default' />
      </StyledIconContainer>
    ),
    ...props
  });

Dialog.info = ({ iconPlacement = 'left', closeButton = null, ...props }: DialogUtilConfig) =>
  dialog({
    iconPlacement,
    icon: (
      <StyledIconContainer iconPlacement={iconPlacement} tone={BaseColor.primary}>
        <IconMap.Info type='default' />
      </StyledIconContainer>
    ),
    closeButton,
    ...props
  });

Dialog.open = dialog;

Dialog.ResponseAction = DialogResponseAction;

export function isDialogOpened() {
  return Boolean(document.querySelector(`[data-radix-focus-guard]`) && document.querySelector('[role="dialog"]'));
}

export const DialogAction = DialogResponseAction;
